/** Build profiles and their verification settings. */
import { z } from 'zod';
import { ArtifactKind, ExecutionMode, Platform, uuid } from './common.js';
import { ENV_ASSERTIONS_MAX, ENV_PATTERN_MAX_LENGTH, envKey } from './env-vars.js';
import { ProviderConfig } from './providers.js';
import { SubmitSettings } from './stores.js';

const envPattern = z.string().min(1).max(ENV_PATTERN_MAX_LENGTH);
const assertionEnvironments = z.array(z.string().min(1).max(64)).max(20).optional();

/**
 * One `verification.envAssertions[]` item (schemas/hangar-config.schema.json `$defs/verification`): `{key, pattern}`
 * tests the resolved value of `key`; `{artifactContains}` requires the artifact to contain a literal or a `${KEY}`.
 * `{key}` without `pattern` is accepted (the console saves it for a row whose pattern is blank) and checks nothing.
 */
export const EnvAssertionWrite = z.union([
  z.object({ key: envKey, pattern: envPattern.optional(), environments: assertionEnvironments, blocking: z.boolean().optional() }).strict(),
  z.object({ artifactContains: z.string().min(1).max(500), environments: assertionEnvironments, blocking: z.boolean().optional() }).strict(),
]);
export type EnvAssertionWrite = z.infer<typeof EnvAssertionWrite>;

const sizeBudget = z.number().gt(0).max(100_000);

/**
 * A profile's `verification` (the post-build checks of packages/domain/src/checks.ts). Strict: the worker's verifier
 * reads it as typed data, so a misspelled or malformed field is refused when it is saved instead of breaking the check.
 * `signing`: `store_app` (the console) and `match_store_upload_cert` (hangar.yml) both compare the store's upload
 * certificate; `none` skips it. `bundleId` is the console's record of the bundle id the profile builds.
 */
export const ProfileVerification = z.object({
  sizeBudgetMb: z.union([sizeBudget, z.object({ android: sizeBudget.optional(), ios: sizeBudget.optional() }).strict()]).optional(),
  signing: z.enum(['store_app', 'match_store_upload_cert', 'none']).optional(),
  envAssertions: z.array(EnvAssertionWrite).max(ENV_ASSERTIONS_MAX).optional(),
  bundleIdMatchesStoreApp: z.boolean().optional(),
  versionMonotonic: z.boolean().optional(),
  bundleId: z.string().min(1).max(255).optional(),
}).strict();
export type ProfileVerification = z.infer<typeof ProfileVerification>;

export const ProfileEnvironmentBinding = z.object({
  environmentId: uuid,
  providerConfig: ProviderConfig.optional(),
  defaultStoreAppId: uuid.nullable().optional(),
  /** How builds of this profile in this environment are submitted (P29); needs `defaultStoreAppId`. */
  submit: SubmitSettings.nullable().optional(),
});
/**
 * A profile's `versionStrategy`, in either vocabulary: the API's (`{versionName: 'template', template}`,
 * `buildNumber: 'from_source'`) or `hangar.yml`'s `versioning` (`{versionName: '0.1.{buildNumber}'}`, `buildNumber: 'source'`).
 * Both are stored as the API's, so a body written from `hangar.yml` is never silently ignored. Templates may use only
 * `{buildNumber}` and `{ciBuildNumber}` (what `versionNameFor` renders); anything else is refused.
 */
const VERSION_TEMPLATE = /^(?:[^{}]|\{(?:buildNumber|ciBuildNumber)\})+$/;
export const VersionStrategyWrite = z.object({
  buildNumber: z.enum(['hangar', 'provider', 'from_source', 'source']).optional(),
  versionName: z.string().min(1).max(100).optional(),
  template: z.string().min(1).max(100).optional(),
}).strict().transform((v, ctx) => {
  const buildNumber = v.buildNumber === 'source' ? 'from_source' : v.buildNumber;
  const named = v.versionName === undefined || v.versionName === 'from_source' || v.versionName === '{source}' || v.versionName === 'template' ? v.versionName : 'template';
  const template = named === 'template' ? (v.versionName === 'template' ? v.template : v.versionName) : undefined;
  if (named === 'template' && (!template || !VERSION_TEMPLATE.test(template))) {
    ctx.addIssue({ code: 'custom', path: [v.versionName === 'template' ? 'template' : 'versionName'], message: 'a version name template may use only {buildNumber} and {ciBuildNumber}, e.g. 0.1.{buildNumber}' });
    return z.NEVER;
  }
  return {
    ...(buildNumber ? { buildNumber } : {}),
    ...(named === undefined ? {} : named === 'template' ? { versionName: 'template' as const, template } : { versionName: 'from_source' as const }),
  };
});
export type VersionStrategyWrite = z.infer<typeof VersionStrategyWrite>;

export const ProfileWrite = z.object({
  slug: z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/),
  name: z.string().min(1).max(120),
  platform: Platform,
  providerAccountId: uuid,
  executionModes: z.array(ExecutionMode).min(1),
  providerConfig: ProviderConfig.default({}),
  artifactKind: ArtifactKind,
  launchForm: z.record(z.string(), z.unknown()).optional(),
  verification: ProfileVerification.optional(),
  versionStrategy: VersionStrategyWrite.optional(),
  submitDefaults: z.record(z.string(), z.unknown()).optional(),
  timeoutMinutes: z.number().int().min(5).max(720).default(120),
  environments: z.array(ProfileEnvironmentBinding).default([]),
});
export type ProfileWrite = z.infer<typeof ProfileWrite>;

/** `ProfilePatch` (merge patch): `slug` and `platform` are immutable. */
export const ProfilePatch = z.object({
  name: z.string().min(1).max(120).optional(),
  providerAccountId: uuid.optional(),
  executionModes: z.array(ExecutionMode).min(1).optional(),
  providerConfig: ProviderConfig.optional(),
  artifactKind: ArtifactKind.optional(),
  launchForm: z.record(z.string(), z.unknown()).optional(),
  verification: ProfileVerification.optional(),
  versionStrategy: VersionStrategyWrite.optional(),
  submitDefaults: z.record(z.string(), z.unknown()).optional(),
  timeoutMinutes: z.number().int().min(5).max(720).optional(),
  environments: z.array(ProfileEnvironmentBinding).optional(),
  archived: z.boolean().optional(),
}).strict();
export type ProfilePatch = z.infer<typeof ProfilePatch>;
